import classnames from "classnames";
import {memo, useCallback, useMemo} from "react";
import type {CalendarDay} from "react-native-ui-datepicker";

import {formatMMddyyyy} from "../../utils";
import {Pressable} from "../ui/pressable";
import {Text} from "../ui/text";
import type {ITextProps} from "../ui/text/types";

import {getDate} from "./util";

type Props = {
    day: CalendarDay;
    onMouseEnter?: (date: Date) => void;
    onSelectDay: (date: Date) => void;
};

const Day: React.FC<Props> = ({day, onMouseEnter, onSelectDay}) => {
    const {color, testID} = useMemo(() => {
        let color: ITextProps["color"] = "blueGray.800";
        let testID: string | undefined = formatMMddyyyy(new Date(day.date));

        if (day.inRange) {
            color = "blueGray.800";
            testID = `${testID}-inRange`;
        }

        if (day.isSelected) {
            color = "blueGray.50";
            testID = `${testID}-selected`;
        }

        // out side
        if (!day.isCurrentMonth) {
            color = "blueGray.400";
        }

        // disabled
        if (day.isDisabled) {
            color = "blueGray.400";
            testID = `${testID}-disabled`;
        }

        return {color, testID};
    }, [day]);

    const handleOnMouseEnter = useCallback(() => {
        const dateValue = getDate(day.date);
        if (dateValue) {
            onMouseEnter?.(dateValue);
        }
    }, [day, onMouseEnter]);

    const handleOnSelectDay = useCallback(() => {
        const dateValue = getDate(day.date);
        if (dateValue) {
            onSelectDay?.(dateValue);
        }
    }, [day, onSelectDay]);

    const textNode = (
        <Text color={color} testID={testID}>
            {day.text}
        </Text>
    );

    const classNames = classnames(
        "w-full h-full flex-1 items-center justify-center",
        day.isToday && "bg-primary-50 rounded-md",
        day.isSelected && "bg-primary-600 rounded-full",
        day.inRange && "bg-primary-50",
        day.rangeStart && "bg-primary-600 rounded-full",
        day.rangeEnd && "bg-primary-600 rounded-full"
    );

    if (process.env.IS_WEB) {
        return (
            <Pressable<"web">
                className={classNames}
                onMouseEnter={handleOnMouseEnter}
                onPress={handleOnSelectDay}
            >
                {textNode}
            </Pressable>
        );
    }

    return (
        <Pressable className={classNames} onPress={handleOnSelectDay}>
            {textNode}
        </Pressable>
    );
};

export default memo(Day);
